<template>
  <v-container fluid>
    <v-row align="center" class="mb-2">
      <v-col>
        <h2 class="headline font-weight-bold">
          <v-icon color="purple darken-2" large class="mr-2">mdi-format-list-bulleted-type</v-icon>
          Catálogo de {{ tituloSingular }} ({{ instancia.toUpperCase() }})
        </h2>
      </v-col>
    </v-row>

    <!-- Formulario para agregar nuevo elemento -->
    <v-card class="mb-4">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-text-field v-model="nuevoItem" :label="'Nuevo nombre de ' + tituloSingular" outlined dense hide-details @keyup.enter="confirmarGuardado" />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" :loading="guardando" @click="confirmarGuardado">
              <v-icon left>mdi-plus</v-icon>Agregar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabla de Elementos -->
    <v-card>
      <v-card-text>
        <v-data-table 
          :headers="headers" 
          :items="items" 
          :loading="cargando" 
          loading-text="Cargando datos..." 
          no-data-text="Sin registros encontrados">
          
          <template v-slot:item.activo="{ item }">
            <v-chip small :color="item.activo ? 'success' : 'grey'" dark>
              {{ item.activo ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </template>

          <template v-slot:item.acciones="{ item }">
            <template v-if="puestosPermitidos">
              <v-btn icon small color="blue" class="mr-2" @click="abrirEdicion(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="red" @click="toggleActivo(item)">
                <v-icon small>{{ item.activo ? 'mdi-close-circle' : 'mdi-check-circle' }}</v-icon>
              </v-btn>
            </template>
            <span v-else class="grey--text caption text-center">Sin permisos</span>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialogo para editar -->
    <v-dialog v-model="dlgEdit" max-width="400" persistent>
      <v-card v-if="itemEdit">
        <v-card-title>Editar {{ tituloSingular }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="itemEdit.nombre" label="Nombre" outlined dense autofocus @keyup.enter="guardarEdicion" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dlgEdit = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="guardando" @click="guardarEdicion">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top right timeout="4000">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';

export default {
  name: 'CatEstados',
  props: {
    instancia: { type: String, required: true }
  },
  watch: {
    instancia() {
      this.cargarDatos();
    }
  },
  data() {
    return {
      tablaAPI: 'estados', // Identificador en la API
      tituloSingular: 'Estado',
      
      items: [],
      cargando: false,
      guardando: false,
      nuevoItem: '',
      
      dlgEdit: false,
      itemEdit: null,

      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Nombre', value: 'nombre' },
        { text: 'Estado', value: 'activo', width: 120 },
        { text: 'Acciones (Admin)', value: 'acciones', width: 140, sortable: false, align: 'center' },
      ],
      snack: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),
    idUsuario() {
      return this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp ? this.getdatosUsuarioERPERP.iderp : 1;
    },
    puestosPermitidos() {
      const puesto = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.idpuesto ? this.getdatosUsuarioERPERP.idpuesto : 0;
      return [12, 48].includes(Number(puesto)) || [2817].includes(Number(this.getdatosUsuarioERPERP.iderp));
    }
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    showSnack(text, color = 'success') {
      this.snack.text = text;
      this.snack.color = color;
      this.snack.show = true;
    },

    async cargarDatos() {
      this.cargando = true;
      try {
        const respuesta = await this.$http.get(`checklist.catalogos.list/${this.tablaAPI}?inst=${this.instancia}`);
        this.items = respuesta.data.data;
      } catch (error) {
        this.showSnack('Error al cargar datos', 'error');
        console.error(error);
      } finally {
        this.cargando = false;
      }
    },

    confirmarGuardado() {
      if (!this.nuevoItem.trim()) return;

      this.$swal.fire({
        title: `¿Confirmar nuevo ${this.tituloSingular}?`,
        text: "Está a punto de crear un nuevo elemento en el catálogo. Una vez creado no podrá ser editado ni eliminado por usuarios comunes.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, crear",
        cancelButtonText: "Cancelar"
      }).then((result) => {
        if (result.isConfirmed) {
          this.agregarInstancia();
        }
      });
    },

    async agregarInstancia() {
      this.guardando = true;
      try {
        const payload = {
          nombre: this.nuevoItem,
          id_usuario: this.idUsuario,
          instancia: this.instancia
        };
        await this.$http.post(`checklist.catalogos.add/${this.tablaAPI}`, payload);
        this.showSnack('Agregado exitosamente');
        this.nuevoItem = '';
        this.cargarDatos();
      } catch (error) {
        this.showSnack((error.response && error.response.data && error.response.data.message) || 'Error al agregar', 'error');
      } finally {
        this.guardando = false;
      }
    },

    abrirEdicion(item) {
      if (!this.puestosPermitidos) return;
      this.itemEdit = { ...item };
      this.dlgEdit = true;
    },

    async guardarEdicion() {
      if (!this.itemEdit.nombre.trim()) return;
      this.guardando = true;
      try {
        const payload = {
          nombre: this.itemEdit.nombre,
          id_usuario: this.idUsuario,
          instancia: this.instancia,
          puestosPermitidos: this.puestosPermitidos
        };
        await this.$http.put(`checklist.catalogos.admin.update/${this.tablaAPI}/${this.itemEdit.id}`, payload);
        this.showSnack('Actualizado correctamente');
        this.dlgEdit = false;
        this.cargarDatos();
      } catch (error) {
        this.showSnack((error.response && error.response.data && error.response.data.message) || 'Error al actualizar', 'error');
      } finally {
        this.guardando = false;
      }
    },

    async toggleActivo(item) {
      if (!this.puestosPermitidos) return;
      const confirmText = item.activo ? 'desactivar' : 'activar';
      
      this.$swal.fire({
        title: `¿${confirmText.toUpperCase()} este ${this.tituloSingular}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Sí, ${confirmText}`
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const payload = {
              activo: item.activo ? 0 : 1,
              id_usuario: this.idUsuario,
              instancia: this.instancia,
              puestosPermitidos: this.puestosPermitidos
            };
            await this.$http.put(`checklist.catalogos.admin.toggle/${this.tablaAPI}/${item.id}`, payload);
            this.showSnack('Cambio de estado exitoso');
            this.cargarDatos();
          } catch (error) {
            this.showSnack((error.response && error.response.data && error.response.data.message) || 'Error al cambiar estado', 'error');
          }
        }
      });
    }

  }
};
</script>
